import { create } from "zustand";
import authService from "@/services/auth/authService";
import { IUser } from "@/services/mocks/userData";
import { hasRole, hasAnyRole } from "@/lib/authUtils";

interface IAuthState {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
}

interface IAuthActions {
  login: (rut: string, password: string) => Promise<boolean>;
  register: (userData: {
    nombre: string;
    apellido: string;
    email: string;
    password: string;
    rut: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

type AuthStore = IAuthState & IAuthActions;

const initialState: IAuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true,
  error: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  initializeAuth: async () => {
    set({ isInitializing: true, error: null });

    try {
      const token = authService.getAccessToken();
      const user = authService.getUser();

      if (!token || !user) {
        set({ isInitializing: false });
        return;
      }

      const { isValid, user: verifiedUser } =
        await authService.verifyAuthStatus();

      if (isValid && verifiedUser) {
        set({
          user: verifiedUser,
          isAuthenticated: true,
          isInitializing: false,
          error: null,
        });
      } else {
        authService.clearSession();
        set({
          user: null,
          isAuthenticated: false,
          isInitializing: false,
          error: null,
        });
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isInitializing: false,
        error: null,
      });
    }
  },

  login: async (rut: string, password: string): Promise<boolean> => {
    if (!rut.trim() || !password.trim()) {
      set({
        error: "Por favor, complete todos los campos.",
        isLoading: false,
      });
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await authService.login(rut, password);

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Cargar el carrito del usuario después del login exitoso
      // Usar import estático para evitar problemas de chunks
      try {
        const { useCartStore } = await import("./cartStore");
        useCartStore.getState().loadCart();
      } catch (error) {
        console.error("Error cargando carrito después del login:", error);
      }

      return true;
    } catch (error: any) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Error de autenticación",
      });
      return false;
    }
  },

  register: async (userData: {
    nombre: string;
    apellido: string;
    email: string;
    password: string;
    rut: string;
  }): Promise<boolean> => {
    if (
      !userData.nombre.trim() ||
      !userData.apellido.trim() ||
      !userData.email.trim() ||
      !userData.password.trim() ||
      !userData.rut.trim()
    ) {
      set({
        error: "Por favor, complete todos los campos.",
        isLoading: false,
      });
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      // Solo registrar el usuario, sin hacer login automático
      await authService.registerOnly(userData);

      set({
        isLoading: false,
        error: null,
      });

      return true;
    } catch (error: any) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : "Error en el registro",
      });
      return false;
    }
  },

  logout: async () => {
    set({ isLoading: true });

    try {
      // Verificar si hay una sesión de caja activa antes del logout
      try {
        const { default: CashSessionService } = await import("@/services/pos/cashSessionService");
        const currentSession = CashSessionService.getCurrentSession();
        
        if (currentSession.isOpen && currentSession.sessionId) {
          // Marcar la sesión como suspendida en lugar de cerrarla forzosamente
          // Esto permite recuperarla posteriormente
          console.log('Sesión de caja detectada al logout, manteniendo para recuperación posterior');
        }
      } catch (error) {
        console.warn('Error verificando sesión de caja al logout:', error);
      }

      await authService.logout();
    } catch (error) {
      // Ignorar errores en logout
    } finally {
      set({
        ...initialState,
        isInitializing: false, // Asegurar que no se quede en estado de inicialización
      });
    }
  },

  refresh: async (): Promise<boolean> => {
    try {
      await authService.refreshToken();
      const user = authService.getUser();

      if (user) {
        set({ user, isAuthenticated: true });
        return true;
      }
      return false;
    } catch (error) {
      set(initialState);
      return false;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  hasRole: (role: string): boolean => {
    const { user } = get();
    if (!user) return false;
    return hasRole(user.roles, role);
  },

  hasAnyRole: (roles: string[]): boolean => {
    const { user } = get();
    if (!user) return false;
    return hasAnyRole(user.roles, roles);
  },
}));

export default useAuthStore;
